import { useIntl } from 'react-intl';
import { Button, Modal, Form, Col, Row } from 'react-bootstrap';
import { Formik, Form as FormikForm } from 'formik';
import { DateTime } from 'luxon';

import { useAxios } from 'configs/requests';
import { DatePickerField } from 'components/Forms';
import { Loading, Error } from 'components/Fetch';
import DebugView from 'components/Forms/DebugView';
import { checkmarkGreen } from 'assets';

interface IResyncDeviceModalProps {
  device: IDeviceListItem;
  onClose: () => void;
  isSuperUser?: boolean;
}

interface IResyncDeviceForm {
  from: Date | null;
  to: Date | null;
}

const RESYNC_MAX_DAYS = 30;

const ResyncDeviceModal = ({ device, onClose, isSuperUser = false }: IResyncDeviceModalProps) => {
  const { formatMessage } = useIntl();

  const limitDateRange = !isSuperUser;
  const today = DateTime.now().startOf('day');
  const minDate = limitDateRange ? today.minus({ days: RESYNC_MAX_DAYS }) : undefined;
  const maxDate = today;

  const initialValues: IResyncDeviceForm = {
    from: null,
    to: null
  };

  const [{ data, loading, error }, post] = useAxios(
    {
      url: `/clinician-gateway/device/${device.deviceId}/sync`,
      method: 'POST'
    },
    { manual: true }
  );

  const validate = (values: IResyncDeviceForm) => {
    const errors: any = {};
    if (!values.from) errors.from = formatMessage({ id: 'FieldNotEmptyKey' });
    if (!values.to) errors.to = formatMessage({ id: 'FieldNotEmptyKey' });
    if (
      values.from &&
      values.to &&
      DateTime.fromJSDate(values.to).startOf('day') <
        DateTime.fromJSDate(values.from).startOf('day')
    ) {
      errors.to = formatMessage({ id: 'endDateBeforeStartDateKey' });
    }
    if (minDate && maxDate) {
      const outOfRange = (value: Date) => {
        const day = DateTime.fromJSDate(value).startOf('day');
        return day < minDate || day > maxDate;
      };
      const rangeError = formatMessage({ id: 'resyncMaxRangeKey' }, { days: RESYNC_MAX_DAYS });
      if (values.from && outOfRange(values.from)) errors.from = rangeError;
      if (values.to && outOfRange(values.to)) errors.to = rangeError;
    }
    return errors;
  };

  const onSubmit = async (values: IResyncDeviceForm) => {
    if (!values.from || !values.to) return;
    const data = {
      from: DateTime.fromJSDate(values.from).toMillis(),
      to: DateTime.fromJSDate(values.to).endOf('day').toMillis()
    };
    await post({ data });
  };

  return (
    <Modal show size="sm" onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{formatMessage({ id: 'resyncDeviceKey' })}</Modal.Title>
      </Modal.Header>
      <Formik initialValues={initialValues} validate={validate} onSubmit={onSubmit}>
        <FormikForm>
          <Modal.Body>
            {loading && <Loading />}
            {error && <Error retry={post} />}
            {data ? (
              <>
                <div className="d-flex mb-3">
                  <p className="m-0 me-2 text-lg">
                    {formatMessage({ id: 'deviceSyncSuccessKey' })}
                  </p>
                  <img src={checkmarkGreen} />
                </div>
                {isSuperUser && <DebugView obj={data} />}
              </>
            ) : (
              <Row>
                {limitDateRange && (
                  <Col md={12}>
                    <p className="text-muted">
                      {formatMessage({ id: 'resyncMaxRangeKey' }, { days: RESYNC_MAX_DAYS })}
                    </p>
                  </Col>
                )}
                <Col md={12}>
                  <Form.Group className="form-group">
                    <DatePickerField
                      wrapperClassName="w-100"
                      label={formatMessage({ id: 'from' })}
                      name="from"
                      minDate={minDate?.toJSDate()}
                      maxDate={maxDate.toJSDate()}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="form-group">
                    <DatePickerField
                      wrapperClassName="w-100"
                      label={formatMessage({ id: 'to' })}
                      name="to"
                      minDate={minDate?.toJSDate()}
                      maxDate={maxDate.toJSDate()}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="link" onClick={onClose}>
              {formatMessage({ id: 'closeKey' })}
            </Button>
            {!data && (
              <Button type="submit" variant="primary" disabled={loading}>
                {formatMessage({ id: 'resyncKey' })}
              </Button>
            )}
          </Modal.Footer>
        </FormikForm>
      </Formik>
    </Modal>
  );
};

export default ResyncDeviceModal;
